// src/rms/pages/ResetPassword.jsx
// ============================================================================
// /reset — choosing a new password
// ============================================================================
// The half of password recovery that was missing. The email existed, the
// request existed, and the link landed people on the marketing page already
// signed in with nowhere to set a password — which is why every report of this
// ended "it just brings me back to the same page".
//
// Mounted OUTSIDE RequireRole. Not because the visitor is signed out — a
// recovery link signs them in before this renders — but because sending them
// through the operator guards would bounce a platform admin or an advertiser
// into the vertical picker on their way to a password field.
//
// ----------------------------------------------------------------------------
// Why there is no token handling here
// ----------------------------------------------------------------------------
// Unlike /join, this page never sees a token. Supabase puts the recovery token
// in the URL FRAGMENT, and the client consumes it during its own startup
// (detectSessionInUrl, see config/supabaseClient.js) before any route renders.
// By the time this component mounts there is either a session or there is not,
// and that is the whole state machine.
//
// The one thing worth reading out of the URL is the opposite case: a dead link
// comes back as #error=access_denied&error_code=otp_expired, and saying "this
// link has expired" is a great deal more use than an empty form that fails on
// submit.
//
// No ad surfaces. A password field is not a place to sell an impression.
// ============================================================================

import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Field, Banner } from "../components/ui";
import Brandmark from "../components/Brandmark";
import i18n from "../i18n";
import "../styles/rms.css";

/** Renders outside RmsProvider, so it translates through i18next directly. */
const t = (key, fallback, params) =>
  i18n.t(key, { defaultValue: fallback, ...(params || {}) });

const MIN_LENGTH = 8;

/**
 * Whatever Supabase says went wrong with the link, from hash or query.
 *
 * Both are checked because the two grant flows report differently: the
 * implicit flow puts it in the fragment, the PKCE exchange in the query
 * string, and which one a project uses is configuration this page should not
 * have an opinion about.
 */
function readLinkError() {
  for (const raw of [
    window.location.hash.replace(/^#/, ""),
    window.location.search.replace(/^\?/, ""),
  ]) {
    if (!raw) continue;
    const params = new URLSearchParams(raw);
    const code = params.get("error_code") || params.get("error");
    if (code) return { code, description: params.get("error_description") || "" };
  }
  return null;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const { isAuthenticated, loading, authEnforced, updatePassword } = useAuth();

  // Read once, before the first paint, and take it out of the address bar.
  // Safe to strip: an error response carries no token, so there is nothing
  // left for the Supabase client to consume. Leaving it would re-report a
  // dead link on every reload of a page the person is trying to use.
  const linkError = useRef(undefined);
  if (linkError.current === undefined) {
    linkError.current = readLinkError();
    if (linkError.current) window.history.replaceState({}, "", window.location.pathname);
  }

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();

    if (password.length < MIN_LENGTH) {
      return setError(t("reset.tooShort", "Use at least 8 characters."));
    }
    if (password !== confirm) {
      return setError(t("reset.mismatch", "Those two passwords are not the same."));
    }

    setBusy(true);
    setError(null);
    const result = await updatePassword(password);
    setBusy(false);

    if (!result.success) return setError(result.error);
    setDone(true);
  };

  // Mobile-first: see the .rms-auth block in styles/rms.css for why this screen
  // does not reuse the console's form sizes. The mark is sized in em so the
  // whole lockup scales from one font-size rather than the word growing and the
  // icon staying put.
  const frame = (children) => (
    <div className="rms-root rms-landing rms-auth">
      <div className="rms-auth-inner">
        <Link to="/" className="rms-auth-brand">
          <Brandmark size="1.35em" />
        </Link>
        {children}
      </div>
    </div>
  );

  // Local backend: no auth server, so there is no password to change. Said
  // plainly rather than rendering a form that cannot do anything, the same way
  // messageService refuses to fake a send.
  if (!authEnforced) {
    return frame(
      <>
        <h1 className="rms-page-title">
          {t("reset.title", "Choose a new password")}
        </h1>
        <Banner tone="info">
          {t("reset.localBackend", "The local backend has no accounts, so there is no password to change. Continue straight to")}{" "}
          <Link to="/rms">{t("reset.theApp", "the app")}</Link>.
        </Banner>
      </>
    );
  }

  if (loading) {
    return frame(
      <p style={{ color: "var(--rms-muted-strong)", fontSize: 14.5 }}>
        {t("reset.checking", "Checking your link…")}
      </p>
    );
  }

  if (done) {
    return frame(
      <>
        <h1 className="rms-page-title">
          {t("reset.doneTitle", "Password changed")}
        </h1>
        <p className="rms-page-sub" style={{ marginBottom: 20 }}>
          {t("reset.doneBody", "You are signed in. Use the new password next time.")}
        </p>
        <Link className="rms-btn rms-btn--primary" to="/rms" style={{ width: "100%", textAlign: "center" }}>
          {t("reset.continue", "Continue to the app")}
        </Link>
      </>
    );
  }

  // No session and no way to make one: the link was used, expired, or was
  // never followed at all. One message for all three — which of them it was is
  // not information worth handing out, and is not useful to the person either,
  // since the answer is the same.
  if (linkError.current || !isAuthenticated) {
    return frame(
      <>
        <h1 className="rms-page-title">
          {t("reset.expiredTitle", "This link has expired")}
        </h1>
        <p className="rms-page-sub" style={{ marginBottom: 20 }}>
          {t("reset.expiredBody", "Recovery links work once and last an hour. Ask for a new one and it will arrive in a moment.")}
        </p>
        <button
          type="button"
          className="rms-btn rms-btn--primary"
          style={{ width: "100%" }}
          onClick={() => navigate("/signin", { state: { mode: "forgot" } })}
        >
          {t("reset.sendAnother", "Send me a new link")}
        </button>
      </>
    );
  }

  return frame(
    <>
      <h1 className="rms-page-title">
        {t("reset.title", "Choose a new password")}
      </h1>
      <p className="rms-page-sub" style={{ marginBottom: 20 }}>
        {t("reset.subtitle", "This replaces the old one everywhere you sign in.")}
      </p>

      <form className="rms-card rms-card-pad" onSubmit={submit}>
        {error ? <Banner tone="danger">{error}</Banner> : null}

        <Field
          label={t("reset.newPassword", "New password")}
          required
          help={t("reset.atLeast", "At least 8 characters.")}
        >
          <input
            className="rms-input"
            type={reveal ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
          />
        </Field>

        <Field label={t("reset.confirmPassword", "Type it again")} required>
          <input
            className="rms-input"
            type={reveal ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </Field>

        <p className="rms-help" style={{ marginBottom: 14 }}>
          <button type="button" className="rms-linkish rms-linkish--tap" onClick={() => setReveal((v) => !v)}>
            {reveal ? t("reset.hide", "Hide the password") : t("reset.show", "Show the password")}
          </button>
        </p>

        <button type="submit" className="rms-btn rms-btn--primary" style={{ width: "100%" }} disabled={busy}>
          {busy ? t("common.working", "Working…") : t("reset.save", "Save new password")}
        </button>
      </form>
    </>
  );
}
